import { createContext, useContext, useState, ReactNode } from "react";

type Language = "fr" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  fr: {
    // Header
    "nav.home": "Accueil",
    "nav.method": "Méthode",
    "nav.services": "Services",
    "nav.cases": "Cas",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.contact": "Contact",
    "cta.audit": "Audit GEO gratuit",
    
    // Hero
    "hero.title": "Référencez votre marque dans les IA génératives",
    "hero.subtitle": "1 personne sur 2 consulte une IA générative avant d'acheter. Le GEO (Generative Engine Optimization) transforme le parcours d'achat. Convertissez les conversations IA en leads qualifiés.",
    "hero.cta.primary": "Demander mon audit GEO gratuit",
    "hero.cta.secondary": "Découvrir notre méthode",
    "hero.trust": "Ils nous font confiance",
    
    // Stats
    "stats.qualified": "+38% de demandes qualifiées en 60 jours",
    "stats.sectors": "12 secteurs accompagnés",
    "stats.roi": "90 jours pour un premier ROI",
    "stats.rgpd": "100% conformité RGPD",
    
    // Approach
    "approach.title": "Une méthodologie conçue pour la performance",
    "approach.subtitle": "Notre approche éprouvée en 3 étapes pour transformer votre visibilité IA en résultats mesurables.",
    "approach.step1.title": "Diagnostic GEO gratuit",
    "approach.step1.desc": "Audit de votre visibilité dans les IA génératives.",
    "approach.step2.title": "Plan d'action sur 90 jours",
    "approach.step2.desc": "Quick wins + roadmap priorisée.",
    "approach.step3.title": "Exécution & mesure",
    "approach.step3.desc": "Mises à jour hebdomadaires et reporting ROI.",
    "approach.cta": "Obtenir mon audit gratuit",
    
    // Services
    "services.title": "Nos services",
    "services.subtitle": "Une gamme complète de solutions GEO pour propulser votre visibilité dans les IA génératives.",
    "services.mapping.title": "Cartographier votre présence IA",
    "services.mapping.desc": "Identifier où et comment votre marque apparaît dans ChatGPT, Gemini et Perplexity.",
    "services.optimization.title": "Optimisation GEO continue",
    "services.optimization.desc": "Ajuster vos contenus et prompts pour maximiser la visibilité non-marque.",
    "services.tracking.title": "Data & tracking GEO",
    "services.tracking.desc": "Tableaux de bord pour suivre les requêtes IA et leads générés.",
    "services.training.title": "Formation & accompagnement",
    "services.training.desc": "Montée en compétence de vos équipes marketing & data.",
    
    // Case Studies
    "cases.title": "Des résultats mesurables",
    "cases.subtitle": "Découvrez comment nous avons aidé nos clients à transformer leur présence dans les IA génératives.",
    "cases.case1.context": "Contexte",
    "cases.case1.context.text": "Marque e-commerce invisible sur les IA génératives.",
    "cases.case1.action": "Action",
    "cases.case1.action.text": "Audit GEO + repositionnement contenu.",
    "cases.case1.result": "Résultat",
    "cases.case1.result.text": "+42% de visibilité IA en 2 mois.",
    "cases.case2.context.text": "Groupe industriel – besoin d'un diagnostic GEO global.",
    "cases.case2.action.text": "Audit + dashboard + plan GEO global.",
    "cases.case2.result.text": "+35% de requêtes captées sur ChatGPT.",
    "cases.cta": "Voir plus de cas",
    
    // Pricing
    "pricing.title": "Nos tarifs",
    "pricing.subtitle": "Des formules adaptées à votre niveau de maturité GEO",
    "pricing.cta": "Commander",
    
    // FAQ
    "faq.title": "Questions fréquentes",
    "faq.subtitle": "Tout ce que vous devez savoir sur le GEO et notre approche.",
    "faq.q1": "Qu'est-ce que le GEO ?",
    "faq.a1": "Le Generative Engine Optimization vise à améliorer votre visibilité dans les IA génératives comme ChatGPT, Gemini ou Perplexity.",
    "faq.q2": "En quoi diffère-t-il du SEO ?",
    "faq.a2": "Le GEO cible les moteurs conversationnels, pas les moteurs de recherche traditionnels.",
    "faq.q3": "Combien de temps pour des résultats ?",
    "faq.a3": "Quick wins visibles en 2 à 4 semaines, ROI complet sous 90 jours.",
    "faq.q4": "Quels outils utilisez-vous ?",
    "faq.a4": "Benchmarks IA, crawlers GEO, dashboards propriétaires, API OpenAI & Gemini.",
    "faq.q5": "Puis-je suivre mes performances ?",
    "faq.a5": "Oui, chaque client dispose d'un tableau de bord GEO personnalisé.",
    "faq.q6": "Mes données sont-elles protégées ?",
    "faq.a6": "100% conformité RGPD, aucune donnée propriétaire utilisée sans consentement.",
    
    // AI Visibility
    "visibility.badge": "Optimisation de référencement nouvelle génération",
    "visibility.title": "Les IA génératives transforment le parcours d'achat.",
    "visibility.title.highlight": "Prenez le contrôle.",
    "visibility.desc": "Chaque jour, les IA apprennent à recommander vos concurrents. La question est : apprennent-elles à vous recommander, vous ? Les outils SEO traditionnels ne vous disent pas comment les IA perçoivent votre marque. GOGENIA.AI comble ce fossé en aidant vos équipes à surveiller et optimiser votre visibilité dans les moteurs de recherche IA.",
    "visibility.cited.title": "Sources citées",
    "visibility.cited.desc": "Tracking en temps réel",
    "visibility.trends.title": "Tendances",
    "visibility.trends.desc": "Analyse prédictive",
    "visibility.competition.title": "Compétition",
    "visibility.competition.desc": "Benchmark IA",
    "visibility.chart.title": "Tendances de Citations",
    "visibility.chart.subtitle": "Suivez la fréquence de citation de votre marque par les LLMs",
    "visibility.top.title": "Top Sources Citantes",
    "visibility.top.citations": "citations",

    // AI Metrics
    "metrics.title": "Métriques de visibilité IA conçues pour les équipes marque modernes",
    "metrics.cta": "Demander une démo",
    "metrics.brand.title": "Visibilité de marque",
    "metrics.brand.desc": "Suivez la fréquence d'apparition de votre marque dans les réponses IA.",
    "metrics.competitor.title": "Suivi concurrentiel",
    "metrics.competitor.desc": "Comparez-vous aux concurrents directs et leaders du secteur.",
    "metrics.competitor.ranking": "Classement sectoriel",
    "metrics.competitor.subtitle": "Marques avec la plus haute visibilité",
    "metrics.competitor.brand": "Marque",
    "metrics.competitor.visibility": "Visibilité",
    "metrics.sentiment.title": "Score de sentiment",
    "metrics.sentiment.desc": "Comprenez le ton et la perception derrière chaque mention.",
    "metrics.sentiment.positive": "Positif",
    "metrics.sentiment.neutral": "Neutre",
    "metrics.sentiment.negative": "Négatif",
    "metrics.ranking.title": "Position dans les classements",
    "metrics.ranking.desc": "Voyez comment votre marque se positionne dans les listes et comparaisons générées par IA.",
    "metrics.prompt.title": "Performance par prompt",
    "metrics.prompt.desc": "Identifiez les requêtes où vous gagnez, et celles où vous êtes en retard.",
    "metrics.prompt.mentions": "mentions",
    "metrics.prompt.avg": "moy",
    
    // Contact
    "contact.title": "Obtenez votre audit GEO gratuit",
    "contact.subtitle": "Remplissez ce formulaire et recevez une analyse complète de votre visibilité dans les IA génératives sous 48h.",
    "contact.name": "Nom",
    "contact.firstname": "Prénom",
    "contact.email": "Email",
    "contact.phone": "Téléphone",
    "contact.company": "Entreprise",
    "contact.jobTitle": "Fonction",
    "contact.message": "Message",
    "contact.message.placeholder": "Décrivez votre projet et vos objectifs GEO...",
    "contact.rgpd": "J'accepte que GOGENIA.AI me contacte au sujet de ma demande.",
    "contact.submit": "Obtenir mon audit gratuit",
    
    // CTA Section
    "cta.title": "Prenez le contrôle de votre visibilité IA",
    "cta.subtitle": "Rejoignez les entreprises qui optimisent leur présence dans les IA génératives et transforment les conversations IA en opportunités concrètes.",
    "cta.button": "Obtenir mon audit gratuit",
    
    // Footer
    "footer.legal": "Mentions légales",
    "footer.privacy": "Politique de confidentialité",
    "footer.copyright": "Copyright GOGENIA.AI 2025",
    "footer.back": "↑ Retour en haut",
    
    // Blog
    "blog.title": "Blog GEO",
    "blog.subtitle": "Actualités, guides et insights sur le Generative Engine Optimization",
    "blog.readmore": "Lire la suite",
    "blog.back": "← Retour au blog",
  },
  en: {
    // Header
    "nav.home": "Home",
    "nav.method": "Method",
    "nav.services": "Services",
    "nav.cases": "Cases",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.contact": "Contact",
    "cta.audit": "Free GEO Audit",
    
    // Hero
    "hero.title": "Get your brand ranked in generative AI",
    "hero.subtitle": "1 in 2 people consult generative AI before buying. GEO (Generative Engine Optimization) is transforming the buying journey. Turn AI conversations into qualified leads.",
    "hero.cta.primary": "Request my free GEO audit",
    "hero.cta.secondary": "Discover our method",
    "hero.trust": "They trust us",
    
    // Stats
    "stats.qualified": "+38% qualified requests in 60 days",
    "stats.sectors": "12 sectors supported",
    "stats.roi": "90 days to first ROI",
    "stats.rgpd": "100% GDPR compliance",
    
    // Approach
    "approach.title": "A methodology designed for performance",
    "approach.subtitle": "Our proven 3-step approach to transform your AI visibility into measurable results.",
    "approach.step1.title": "Free GEO diagnostic",
    "approach.step1.desc": "Audit of your visibility in generative AI.",
    "approach.step2.title": "90-day action plan",
    "approach.step2.desc": "Quick wins + prioritized roadmap.",
    "approach.step3.title": "Execution & measurement",
    "approach.step3.desc": "Weekly updates and ROI reporting.",
    "approach.cta": "Get my free audit",
    
    // Services
    "services.title": "Our services",
    "services.subtitle": "A complete range of GEO solutions to boost your visibility in generative AI.",
    "services.mapping.title": "Map your AI presence",
    "services.mapping.desc": "Identify where and how your brand appears in ChatGPT, Gemini and Perplexity.",
    "services.optimization.title": "Continuous GEO optimization",
    "services.optimization.desc": "Adjust your content and prompts to maximize non-branded visibility.",
    "services.tracking.title": "GEO data & tracking",
    "services.tracking.desc": "Dashboards to track AI queries and generated leads.",
    "services.training.title": "Training & support",
    "services.training.desc": "Upskill your marketing & data teams.",
    
    // Case Studies
    "cases.title": "Measurable results",
    "cases.subtitle": "Discover how we helped our clients transform their presence in generative AI.",
    "cases.case1.context": "Context",
    "cases.case1.context.text": "E-commerce brand invisible on generative AI.",
    "cases.case1.action": "Action",
    "cases.case1.action.text": "GEO audit + content repositioning.",
    "cases.case1.result": "Result",
    "cases.case1.result.text": "+42% AI visibility in 2 months.",
    "cases.case2.context.text": "Industrial group – need for global GEO diagnostic.",
    "cases.case2.action.text": "Audit + dashboard + global GEO plan.",
    "cases.case2.result.text": "+35% queries captured on ChatGPT.",
    "cases.cta": "See more cases",
    
    // Pricing
    "pricing.title": "Our pricing",
    "pricing.subtitle": "Plans tailored to your GEO maturity level",
    "pricing.cta": "Order",
    
    // FAQ
    "faq.title": "Frequently asked questions",
    "faq.subtitle": "Everything you need to know about GEO and our approach.",
    "faq.q1": "What is GEO?",
    "faq.a1": "Generative Engine Optimization aims to improve your visibility in generative AI like ChatGPT, Gemini or Perplexity.",
    "faq.q2": "How does it differ from SEO?",
    "faq.a2": "GEO targets conversational engines, not traditional search engines.",
    "faq.q3": "How long to see results?",
    "faq.a3": "Quick wins visible in 2 to 4 weeks, full ROI within 90 days.",
    "faq.q4": "What tools do you use?",
    "faq.a4": "AI benchmarks, GEO crawlers, proprietary dashboards, OpenAI & Gemini APIs.",
    "faq.q5": "Can I track my performance?",
    "faq.a5": "Yes, each client has a personalized GEO dashboard.",
    "faq.q6": "Is my data protected?",
    "faq.a6": "100% GDPR compliance, no proprietary data used without consent.",
    
    // AI Visibility
    "visibility.badge": "Next-generation search optimization",
    "visibility.title": "Generative AI is transforming the buyer journey.",
    "visibility.title.highlight": "Take control.",
    "visibility.desc": "Every day, AI learns to recommend your competitors. The question is: is it learning to recommend you? Traditional SEO tools don't tell you how AI perceives your brand. GOGENIA.AI bridges this gap by helping your teams monitor and optimize your visibility in AI-powered search engines.",
    "visibility.cited.title": "Cited Sources",
    "visibility.cited.desc": "Real-time tracking",
    "visibility.trends.title": "Trends",
    "visibility.trends.desc": "Predictive analysis",
    "visibility.competition.title": "Competition",
    "visibility.competition.desc": "AI Benchmark",
    "visibility.chart.title": "Citation Trends",
    "visibility.chart.subtitle": "Track how often your brand is cited by LLMs",
    "visibility.top.title": "Top Citing Sources",
    "visibility.top.citations": "citations",

    // AI Metrics
    "metrics.title": "AI Visibility Metrics Designed for Modern Brand Teams",
    "metrics.cta": "Get a demo",
    "metrics.brand.title": "Brand Visibility",
    "metrics.brand.desc": "Know how often your brand appears in responses.",
    "metrics.competitor.title": "Competitor Tracking",
    "metrics.competitor.desc": "Benchmark against direct competitors and industry leaders.",
    "metrics.competitor.ranking": "Industry ranking",
    "metrics.competitor.subtitle": "Brands with the highest visibility",
    "metrics.competitor.brand": "Brand",
    "metrics.competitor.visibility": "Visibility",
    "metrics.sentiment.title": "Sentiment Score",
    "metrics.sentiment.desc": "Understand the tone and perception behind each mention.",
    "metrics.sentiment.positive": "Positive",
    "metrics.sentiment.neutral": "Neutral",
    "metrics.sentiment.negative": "Negative",
    "metrics.ranking.title": "Ranking Position",
    "metrics.ranking.desc": "See how your brand stacks up in AI-generated lists and comparisons.",
    "metrics.prompt.title": "Prompt Performance",
    "metrics.prompt.desc": "Track which prompts you're winning, and where you're falling behind.",
    "metrics.prompt.mentions": "mentions",
    "metrics.prompt.avg": "avg",
    
    // Contact
    "contact.title": "Get your free GEO audit",
    "contact.subtitle": "Fill out this form and receive a complete analysis of your visibility in generative AI within 48 hours.",
    "contact.name": "Last name",
    "contact.firstname": "First name",
    "contact.email": "Email",
    "contact.phone": "Phone",
    "contact.company": "Company",
    "contact.jobTitle": "Job title",
    "contact.message": "Message",
    "contact.message.placeholder": "Describe your project and your GEO objectives...",
    "contact.rgpd": "I agree that GOGENIA.AI may contact me regarding my request.",
    "contact.submit": "Request my free audit",
    
    // CTA Section
    "cta.title": "Take control of your AI visibility",
    "cta.subtitle": "Join companies optimizing their presence in generative AI and transforming AI conversations into concrete opportunities.",
    "cta.button": "Get my free audit",
    
    // Footer
    "footer.legal": "Legal notice",
    "footer.privacy": "Privacy policy",
    "footer.copyright": "Copyright GOGENIA.AI 2025",
    "footer.back": "↑ Back to top",
    
    // Blog
    "blog.title": "GEO Blog",
    "blog.subtitle": "News, guides and insights on Generative Engine Optimization",
    "blog.readmore": "Read more",
    "blog.back": "← Back to blog",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("fr");

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.fr] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
